import React, { useState, useRef } from 'react';
import { useI18n } from './i18n';
import { authFetch } from './auth';
import { buildSheetPath, sheetUrl, LINK_ROUTES } from './deepLinks';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

// Link to a sheet / pairing: "Copy link" (clipboard with a fallback), "Share…" (Web Share API when the browser has it) and a note on who
// can open the link, according to the visibility of the sheet.
// Props: type (deep-link type: onav, aspi, pairing, food, coffee, oil, tea, vinegar), id, visibility ('private' | 'public' | 'shared'),
// isOwner (the viewer owns the sheet: only then the private-sheet note and the "Make public" button appear), onVisibilityChange(visibility).
function ShareLinkButton({ type, id, visibility, isOwner = false, onVisibilityChange }) {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);                     // { kind: 'ok' | 'error', text }
  const [localVisibility, setLocalVisibility] = useState(null);   // set after "Make public"
  const [manual, setManual] = useState(false);                    // clipboard not available: show the link to copy by hand
  const input = useRef(null);
  const path = buildSheetPath(type, id);
  if (!path) return null;
  const url = sheetUrl(window.location.origin, type, id);
  const current = localVisibility || visibility;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const area = document.createElement('textarea');
        area.value = url;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const done = document.execCommand && document.execCommand('copy');
        document.body.removeChild(area);
        if (!done) throw new Error('copy failed');
      }
      setManual(false);
      setStatus({ kind: 'ok', text: t('app.share.copied') });
    } catch {
      setManual(true);                                             // the user can select and copy the link by hand
      setStatus({ kind: 'error', text: t('app.share.copy_error') });
      setTimeout(() => input.current && input.current.select(), 50);
    }
  };

  const share = async () => {
    try {
      await navigator.share({ title: t('app.site_name'), url });
    } catch { /* cancelled by the user: nothing to report */ }
  };

  const makePublic = async () => {
    try {
      const res = await authFetch(`${API_URL}${LINK_ROUTES[type]}/${id}/visibility`, { method: 'PUT', body: JSON.stringify({ visibility: 'public' }) });
      if (!res || !res.ok) throw new Error();
      setLocalVisibility('public');
      setStatus({ kind: 'ok', text: t('app.share.made_public') });
      if (onVisibilityChange) onVisibilityChange('public');
    } catch {
      setStatus({ kind: 'error', text: t('app.share.make_public_error') });
    }
  };

  return (
    <div className="share-link">
      <span className="share-link-actions">
        <button type="button" className="chip" onClick={copy} title={url}>{t('app.share.copy')}</button>
        {canShare && <button type="button" className="chip" onClick={share}>{t('app.share.share')}</button>}
        {status && <span className={`share-link-status ${status.kind}`} role="status">{status.text}</span>}
      </span>
      {manual && (
        <input ref={input} className="share-link-url" readOnly value={url} aria-label={t('app.share.link_label')} onFocus={(e) => e.target.select()} />
      )}
      {isOwner && current === 'private' && (
        <span className="share-link-note">
          {t('app.share.private_note')}{' '}
          <button type="button" className="chip" onClick={makePublic}>{t('app.share.make_public')}</button>
        </span>
      )}
      {current === 'shared' && <span className="share-link-note">{t('app.share.shared_note')}</span>}
    </div>
  );
}

export default ShareLinkButton;
